import classnames from "classnames";
import Papa from "papaparse";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Col, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap";

import { useProfile } from "Components/Hooks/UserHooks";
import { importIndividual } from "common/services/individuals.service";
import {
  FieldMapping,
  ImportResults,
  ImportStatus,
  INDIVIDUAL_FIELDS,
  IndividualFieldKey,
  MAX_IMPORT_RECORDS,
  autoMapFields,
  mapRowToRecord,
} from "./individualImportFields";

const IMPORT_TIMEOUT_MS = 5 * 60 * 1000;
const PREVIEW_LIMIT = 10;

const SectionHeader: React.FC<{
  icon: string;
  title: string;
  description: React.ReactNode;
  step: number;
}> = ({ icon, title, description, step }) => (
  <div
    className="d-flex align-items-start gap-3 mb-4 pb-3"
    style={{ borderBottom: "1px solid #eef0f4" }}
  >
    <div
      className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0 bg-primary-subtle"
      style={{ width: 38, height: 38 }}
    >
      <i className={classnames(icon, "text-primary")} style={{ fontSize: 18 }} />
    </div>
    <div className="flex-grow-1">
      <h5 className="mb-0 fw-semibold">{title}</h5>
      <p className="text-muted mb-0 mt-1">{description}</p>
    </div>
    <span className="badge bg-primary-subtle text-primary rounded-pill px-3 py-2 fs-12 flex-shrink-0 align-self-center">
      Step {step} of 4
    </span>
  </div>
);

const ImportSummaryItem: React.FC<{
  icon: string;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => {
  return (
    <div className="d-flex align-items-center gap-3 px-4 py-3 h-100">
      <i className={`${icon} text-secondary`} style={{ fontSize: 22 }} />
      <div>
        <div className="text-muted mb-1" style={{ fontSize: 12 }}>
          {label}
        </div>
        <div className="fw-semibold" style={{ fontSize: 20, lineHeight: 1.2, color: "#1e293b" }}>
          {value}
        </div>
      </div>
    </div>
  );
};

const ResultMetric: React.FC<{
  icon: string;
  label: string;
  value: number;
  tone: "secondary" | "success" | "danger";
}> = ({ icon, label, value, tone }) => {
  const colorClass = {
    secondary: "text-secondary",
    success: "text-success",
    danger: "text-danger",
  }[tone];

  return (
    <div className="d-flex align-items-center gap-3 px-4 py-3 h-100">
      <i className={`${icon} ${colorClass}`} style={{ fontSize: 22 }} />
      <div>
        <div className="text-muted mb-1" style={{ fontSize: 12 }}>
          {label}
        </div>
        <div className="fw-semibold" style={{ fontSize: 20, lineHeight: 1.2, color: "#1e293b" }}>
          {value.toLocaleString()}
        </div>
      </div>
    </div>
  );
};

const IndividualsImportWizard: React.FC<{ onImported?: () => void }> = ({
  onImported,
}) => {
  const { token } = useProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const downloadUrlRef = useRef<string | null>(null);

  const [activeTab, setActiveTab] = useState(1);
  const [passedSteps, setPassedSteps] = useState<number[]>([1]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [csvParseError, setCsvParseError] = useState<string | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping>({});
  const [importStatus, setImportStatus] = useState<ImportStatus>("idle");
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResults, setImportResults] = useState<ImportResults | null>(null);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  const fileReady = !!selectedFile && csvRows.length > 0 && !csvParseError;
  const requiredFieldsMapped = INDIVIDUAL_FIELDS.filter((field) => field.required).every(
    (field) => !!fieldMappings[field.key]
  );

  const stopProgressSimulation = useCallback(() => {
    if (progressIntervalRef.current !== null) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const revokeDownloadUrl = useCallback(() => {
    if (downloadUrlRef.current) {
      URL.revokeObjectURL(downloadUrlRef.current);
      downloadUrlRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      stopProgressSimulation();
      revokeDownloadUrl();
    },
    [revokeDownloadUrl, stopProgressSimulation]
  );

  const toggleTab = (tab: number) => {
    if (tab < 1 || tab > 4 || tab === activeTab) return;
    if (tab > 1 && !fileReady) return;
    if (tab > 2 && !requiredFieldsMapped) return;
    if (tab > activeTab + 1 && !passedSteps.includes(tab)) return;
    setActiveTab(tab);
    setPassedSteps((previous) =>
      previous.includes(tab) ? previous : [...previous, tab]
    );
  };

  const resetImportState = () => {
    stopProgressSimulation();
    revokeDownloadUrl();
    setImportStatus("idle");
    setImportProgress({ current: 0, total: 0 });
    setImportResults(null);
    setImportErrors([]);
  };

  const handleStartOver = () => {
    resetImportState();
    setSelectedFile(null);
    setIsDragActive(false);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvParseError(null);
    setFieldMappings({});
    setActiveTab(1);
    setPassedSteps([1]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePickedFiles = (files: FileList | File[]) => {
    const file = Array.isArray(files) ? files[0] : files?.[0];
    if (!file) return;

    resetImportState();
    setSelectedFile(file);
    setCsvHeaders([]);
    setCsvRows([]);
    setFieldMappings({});

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setCsvParseError("Please upload a CSV file.");
      return;
    }

    setCsvParseError(null);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        const headers = (results.meta.fields ?? []).filter(Boolean);
        if (headers.length === 0) {
          setCsvParseError("No column headers were found in this CSV file.");
          return;
        }
        if (results.data.length === 0) {
          setCsvParseError("The CSV file does not contain any data rows.");
          return;
        }
        if (results.errors.some((error) => error.type === "Quotes")) {
          setCsvParseError("The CSV contains an invalid quoted value. Please check its format.");
          return;
        }
        setCsvHeaders(headers);
        setCsvRows(results.data);
        setFieldMappings(autoMapFields(headers));
      },
      error: (error) => setCsvParseError(error.message),
    });
  };

  const runImport = async () => {
    const rowsToImport = csvRows.slice(0, MAX_IMPORT_RECORDS);
    const total = rowsToImport.length;
    setImportStatus("running");
    setImportErrors([]);
    setImportProgress({ current: 0, total });

    let simulatedPercent = 0;
    progressIntervalRef.current = setInterval(() => {
      simulatedPercent += (90 - simulatedPercent) * 0.07;
      setImportProgress({
        current: Math.round((simulatedPercent / 100) * total),
        total,
      });
    }, 400);

    try {
      const records = rowsToImport.map((row) => mapRowToRecord(row, fieldMappings));
      const result = await importIndividual(
        { data: records },
        token,
        { timeoutMs: IMPORT_TIMEOUT_MS }
      );
      const successful = result.metadata?.data?.successful ?? 0;
      const failed = result.metadata?.data?.failed ?? total - successful;

      revokeDownloadUrl();
      const downloadUrl =
        failed > 0 && result.blob?.size > 0
          ? URL.createObjectURL(result.blob)
          : null;
      downloadUrlRef.current = downloadUrl;
      setImportResults({ total, successful, failed, downloadUrl });
      if (successful > 0) onImported?.();
    } catch (error: any) {
      const isTimeout = error?.code === "ECONNABORTED" || error?.name === "AbortError";
      const message = isTimeout
        ? "The import request timed out. Try importing fewer rows."
        : error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Import request failed.";
      setImportErrors([message]);
      setImportResults({ total, successful: 0, failed: total, downloadUrl: null });
    } finally {
      stopProgressSimulation();
      setImportProgress({ current: total, total });
      setImportStatus("done");
    }
  };

  const steps = [
    { tab: 1, label: "File Upload", icon: "ri-upload-cloud-2-line" },
    { tab: 2, label: "Field Mapping", icon: "ri-git-merge-line" },
    { tab: 3, label: "Data Preview", icon: "ri-eye-line" },
    { tab: 4, label: "Import", icon: "ri-checkbox-circle-line" },
  ];
  const mappedFields = INDIVIDUAL_FIELDS.filter((field) => fieldMappings[field.key]);
  const requiredFields = INDIVIDUAL_FIELDS.filter((field) => field.required);
  const mappedRequiredFields = requiredFields.filter((field) => fieldMappings[field.key]);
  const effectiveRows = Math.min(csvRows.length, MAX_IMPORT_RECORDS);
  const progressPercent = importProgress.total
    ? Math.round((importProgress.current / importProgress.total) * 100)
    : 0;

  return (
    <>
      <div className="step-arrow-nav mb-4">
        <Nav className="nav-pills nav-justified custom-nav" role="tablist">
          {steps.map(({ tab, label, icon }) => {
            const locked =
              (tab > 1 && !fileReady) ||
              (tab > 2 && !requiredFieldsMapped) ||
              (tab > activeTab + 1 && !passedSteps.includes(tab));
            return (
              <NavItem key={tab}>
                <NavLink
                  href="#"
                  className={classnames(
                    { active: activeTab === tab, done: activeTab > tab, "text-muted": locked },
                    "p-3 fs-15 d-flex align-items-center justify-content-center gap-2"
                  )}
                  style={locked ? { pointerEvents: "none", opacity: 0.45 } : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    toggleTab(tab);
                  }}
                >
                  <span
                    className={classnames(
                      "d-inline-flex align-items-center justify-content-center rounded-circle",
                      locked
                        ? "bg-light text-muted"
                        : activeTab > tab
                          ? "bg-success-subtle text-success"
                          : "bg-primary-subtle text-primary"
                    )}
                    style={{ width: 30, height: 30 }}
                  >
                    <i className={activeTab > tab ? "ri-check-line" : locked ? "ri-lock-line" : icon} />
                  </span>
                  <span className="d-none d-sm-inline">{label}</span>
                </NavLink>
              </NavItem>
            );
          })}
        </Nav>
      </div>

      <TabContent activeTab={activeTab} className="pt-3">
        <TabPane tabId={1}>
          <SectionHeader
            icon="ri-upload-cloud-2-line"
            title="File Upload"
            description="Upload a CSV file containing your individual records. The first row must contain column names."
            step={1}
          />
          <div
            role="button"
            tabIndex={0}
            className="w-100 d-flex flex-column align-items-center justify-content-center"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") fileInputRef.current?.click();
            }}
            onDragEnter={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              event.preventDefault();
              setIsDragActive(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              handlePickedFiles(event.dataTransfer.files);
            }}
            style={{
              minHeight: 260,
              padding: "32px 24px",
              borderRadius: 10,
              cursor: "pointer",
              background: isDragActive ? "#f0f4ff" : fileReady ? "#f8fff8" : "#f8f9fc",
              border: `2px dashed ${
                isDragActive ? "#4361ee" : csvParseError ? "#ef4444" : fileReady ? "#22c55e" : "#d1d9e6"
              }`,
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="d-none"
              onChange={(event) => {
                if (event.target.files?.length) handlePickedFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <div
              className="d-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ width: 68, height: 68, background: csvParseError ? "#fee2e2" : fileReady ? "#dcfce7" : "#f1f5f9" }}
            >
              <i
                className={csvParseError ? "ri-error-warning-line" : fileReady ? "ri-file-text-line" : "ri-upload-cloud-2-line"}
                style={{ fontSize: 32, color: csvParseError ? "#ef4444" : fileReady ? "#16a34a" : "#64748b" }}
              />
            </div>
            {selectedFile ? (
              <>
                <div className="fw-semibold">{selectedFile.name}</div>
                <div className="text-muted mt-1">
                  {(selectedFile.size / 1024).toFixed(2)} KB
                  {fileReady && ` · ${csvRows.length.toLocaleString()} rows detected`}
                </div>
                <small className="text-muted mt-3">Click to replace this file</small>
              </>
            ) : (
              <>
                <div className="fw-semibold text-muted">
                  {isDragActive ? "Drop your file here" : "Drag & drop your CSV file here"}
                </div>
                <div className="text-muted mt-1">or click to browse</div>
                <span className="badge bg-secondary mt-3">CSV</span>
              </>
            )}
          </div>
          {csvParseError && (
            <Alert color="danger" className="mt-3 mb-0">
              <i className="ri-error-warning-line me-2" />
              {csvParseError}
            </Alert>
          )}
          <div className="d-flex align-items-center gap-2 mt-4 pt-3 border-top">
            <a
              href={`${process.env.PUBLIC_URL}/templates/individuals_import_template.csv`}
              download="individuals_import_template.csv"
              className="btn btn-soft-primary btn-sm"
            >
              <i className="ri-download-2-line me-1" />
              Download Template
            </a>
            <button type="button" className="btn btn-ghost-secondary btn-sm" onClick={handleStartOver}>
              <i className="ri-restart-line me-1" />
              Start Over
            </button>
            <button
              type="button"
              className="btn btn-primary btn-label right ms-auto"
              disabled={!fileReady}
              onClick={() => toggleTab(2)}
            >
              <i className="ri-git-merge-line label-icon align-middle fs-16 ms-2" />
              Proceed to Field Mapping
            </button>
          </div>
        </TabPane>

        <TabPane tabId={2}>
          <SectionHeader
            icon="ri-git-merge-line"
            title="Field Mapping"
            description="Match the columns in your CSV file to the corresponding individual fields."
            step={2}
          />
          <div className="d-flex align-items-center gap-3 rounded-2 px-3 py-2 mb-3 bg-light border">
            <span><strong>{mappedFields.length}</strong> of <strong>{INDIVIDUAL_FIELDS.length}</strong> fields mapped</span>
            <span className="text-muted">Fields marked <span className="text-danger">*</span> are required.</span>
          </div>
          <div className="rounded-3 border overflow-hidden">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="py-3 ps-3">Individual Field</th>
                  <th className="py-3">CSV Column</th>
                  <th className="py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {INDIVIDUAL_FIELDS.map((field) => {
                  const mapped = !!fieldMappings[field.key];
                  return (
                    <tr key={field.key}>
                      <td className="ps-3 fw-medium">
                        {field.label}{field.required && <span className="text-danger ms-1">*</span>}
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          style={{ maxWidth: 320 }}
                          value={fieldMappings[field.key] ?? ""}
                          onChange={(event) => {
                            const value = event.target.value;
                            setFieldMappings((previous) => {
                              const next = { ...previous };
                              if (value) next[field.key as IndividualFieldKey] = value;
                              else delete next[field.key as IndividualFieldKey];
                              return next;
                            });
                          }}
                        >
                          <option value="">— Not mapped —</option>
                          {csvHeaders.map((header) => <option key={header} value={header}>{header}</option>)}
                        </select>
                      </td>
                      <td className="text-center">
                        <span className={`badge rounded-pill ${mapped ? "bg-success-subtle text-success" : "bg-light text-muted"}`}>
                          {mapped ? "Mapped" : "Skipped"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!requiredFieldsMapped && (
            <div className="text-danger mt-2">
              <i className="ri-error-warning-line me-1" />Full Name must be mapped to continue.
            </div>
          )}
          <div className="d-flex mt-4 pt-3 border-top">
            <button className="btn btn-light" type="button" onClick={() => toggleTab(1)}>Back</button>
            <button
              className="btn btn-primary ms-auto"
              type="button"
              disabled={!requiredFieldsMapped}
              onClick={() => toggleTab(3)}
            >
              Preview Data <i className="ri-arrow-right-line ms-1" />
            </button>
          </div>
        </TabPane>

        <TabPane tabId={3}>
          <SectionHeader
            icon="ri-eye-line"
            title="Data Preview"
            description={<>Showing the first {Math.min(PREVIEW_LIMIT, csvRows.length)} of <strong>{csvRows.length.toLocaleString()}</strong> rows.</>}
            step={3}
          />
          <div className="rounded-3 border" style={{ maxHeight: 380, overflow: "auto" }}>
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th className="ps-3">#</th>
                  {mappedFields.map((field) => (
                    <th key={field.key} style={{ whiteSpace: "nowrap" }}>
                      {field.label}
                      <div className="fw-normal text-muted fs-12">← {fieldMappings[field.key]}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvRows.slice(0, PREVIEW_LIMIT).map((row, index) => (
                  <tr key={index}>
                    <td className="ps-3 text-muted">{index + 1}</td>
                    {mappedFields.map((field) => {
                      const column = fieldMappings[field.key];
                      const value = column ? row[column] : "";
                      return <td key={field.key} title={value}>{value || "—"}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="d-flex mt-4 pt-3 border-top">
            <button className="btn btn-light" type="button" onClick={() => toggleTab(2)}>Back</button>
            <button className="btn btn-primary ms-auto" type="button" onClick={() => toggleTab(4)}>
              Proceed to Import <i className="ri-upload-2-line ms-1" />
            </button>
          </div>
        </TabPane>

        <TabPane tabId={4}>
          {importStatus === "idle" && (
            <>
              <SectionHeader
                icon="ri-upload-2-line"
                title="Ready to Import"
                description="Review the summary below, then start the import."
                step={4}
              />
              {csvRows.length > MAX_IMPORT_RECORDS && (
                <Alert color="warning">
                  Only the first {MAX_IMPORT_RECORDS.toLocaleString()} of {csvRows.length.toLocaleString()} rows will be imported. Split the file to import the remainder.
                </Alert>
              )}
              <div className="border rounded-3 mb-4 overflow-hidden">
                <Row className="g-0">
                <Col md={4} className="border-end">
                  <ImportSummaryItem
                    icon="ri-stack-line"
                    label="Rows to import"
                    value={effectiveRows.toLocaleString()}
                  />
                </Col>
                <Col md={4} className="border-end">
                  <ImportSummaryItem
                    icon="ri-node-tree"
                    label="Fields mapped"
                    value={
                      <>
                        {mappedFields.length}
                        <span className="text-muted fw-normal ms-1" style={{ fontSize: 15 }}>
                          / {INDIVIDUAL_FIELDS.length}
                        </span>
                      </>
                    }
                  />
                </Col>
                <Col md={4}>
                  <ImportSummaryItem
                    icon="ri-list-check-3"
                    label="Required fields"
                    value={
                      <>
                        {mappedRequiredFields.length}
                        <span className="text-muted fw-normal ms-1" style={{ fontSize: 15 }}>
                          / {requiredFields.length}
                        </span>
                      </>
                    }
                  />
                </Col>
                </Row>
              </div>
              <div className="d-flex pt-3 border-top">
                <button className="btn btn-light" type="button" onClick={handleStartOver}>Start Over</button>
                <button className="btn btn-primary ms-auto" type="button" onClick={runImport}>
                  Start Import <i className="ri-upload-2-line ms-1" />
                </button>
              </div>
            </>
          )}

          {importStatus === "running" && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary mb-3" style={{ width: 64, height: 64 }} role="status" />
              <h5>Importing individuals…</h5>
              <p className="text-muted">Please keep this window open while the records are processed.</p>
              <div className="mx-auto" style={{ maxWidth: 460 }}>
                <div className="d-flex justify-content-between mb-2"><span>Progress</span><strong>{progressPercent}%</strong></div>
                <div className="progress" style={{ height: 8 }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${progressPercent}%` }} />
                </div>
              </div>
            </div>
          )}

          {importStatus === "done" && importResults && (
            <div className="py-3 mx-auto" style={{ maxWidth: 820 }}>
              <div className="d-flex align-items-center gap-3 mb-4">
                <div
                  className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
                    importResults.failed
                      ? "bg-warning-subtle text-warning"
                      : "bg-success-subtle text-success"
                  }`}
                  style={{ width: 52, height: 52 }}
                >
                  <i
                    className={importResults.failed ? "ri-alert-line" : "ri-check-line"}
                    style={{ fontSize: 25 }}
                  />
                </div>
                <div className="flex-grow-1">
                  <h5 className="mb-1 fw-semibold">
                    {importResults.failed ? "Import completed with errors" : "Import successful"}
                  </h5>
                  <p className="text-muted mb-0">
                    {importResults.failed
                      ? `${importResults.successful.toLocaleString()} of ${importResults.total.toLocaleString()} records imported successfully.`
                      : `All ${importResults.total.toLocaleString()} records imported successfully.`}
                  </p>
                </div>
                <span
                  className={`badge rounded-pill px-3 py-2 ${
                    importResults.failed
                      ? "bg-warning-subtle text-warning"
                      : "bg-success-subtle text-success"
                  }`}
                >
                  {importResults.failed ? "Needs review" : "Completed"}
                </span>
              </div>

              <div className="border rounded-3 overflow-hidden mb-4">
                <Row className="g-0">
                  <Col md={4} className="border-end">
                    <ResultMetric
                      icon="ri-stack-line"
                      label="Records processed"
                      value={importResults.total}
                      tone="secondary"
                    />
                  </Col>
                  <Col md={4} className="border-end">
                    <ResultMetric
                      icon="ri-user-follow-line"
                      label="Imported"
                      value={importResults.successful}
                      tone="success"
                    />
                  </Col>
                  <Col md={4}>
                    <ResultMetric
                      icon="ri-file-warning-line"
                      label="Failed"
                      value={importResults.failed}
                      tone="danger"
                    />
                  </Col>
                </Row>
              </div>

              {importResults.failed > 0 && importResults.downloadUrl && (
                <div className="d-flex align-items-center gap-3 border rounded-3 p-3 mb-4">
                  <i className="ri-file-download-line text-secondary fs-4 flex-shrink-0" />
                  <div className="flex-grow-1">
                    <div className="fw-medium">Review failed records</div>
                    <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                      Download the CSV to see the error for each failed row, correct it, and import again.
                    </div>
                  </div>
                  <a
                    className="btn btn-outline-danger flex-shrink-0"
                    href={importResults.downloadUrl}
                    download="Failed_Individuals_Import.csv"
                  >
                    <i className="ri-download-2-line me-1" />
                    Download CSV
                  </a>
                </div>
              )}
              {importErrors.length > 0 && (
                <Alert color="danger" className="text-start mb-4">
                  {importErrors.map((error, index) => <div key={index}>{error}</div>)}
                </Alert>
              )}
              <div className="d-flex align-items-center pt-3 border-top">
                <button className="btn btn-light" type="button" onClick={handleStartOver}>
                  <i className="ri-restart-line me-1" />
                  Import Another File
                </button>
              </div>
            </div>
          )}
        </TabPane>
      </TabContent>
    </>
  );
};

export default IndividualsImportWizard;
